import { computed, inject, Injectable } from '@angular/core';
import { WirelessAdditionalOfferData } from '../../../../../../models/order/workflow/wireless/wireless-additional-offer.model';
import { WirelessFlowStateService } from './wireless-flow-state.service';
import { WirelessProductResponseService } from '../../order-api-response/wireless-pd-response.service';
import { BaseWorkflowStateService } from '../base-workflow-state.service';
import { AdditionalOfferData, OfferType } from '../../../../../../models/order/workflow/additional-offer.model';



@Injectable({ providedIn: 'root' })
export class WirelessAdditionalOffersStateService extends BaseWorkflowStateService<WirelessAdditionalOfferData> {
    private flowStateService = inject(WirelessFlowStateService);
    private wirelessPdResponseService = inject(WirelessProductResponseService);
    override stateKey: string = 'wirelessOffers';
    private readonly ABP_CHOICE_CODE = 'ATT-WIRELESS-ABP-BOTH-YES';
    private readonly PAPERLESS_BILLING_CHOICE_CODE = 'ATT-WIRELESS-PAPERLESS-BILLING-YES';
    customizations = computed(() => this.wirelessPdResponseService.getCustomizations());

    offers = computed(() => {
        return this.state()?.offers ?? [];
    });

    includedOffers = computed(() => {
        return this.state().offers?.filter(offer => offer.type === OfferType.Required) || [];
    });

    specialPromos = computed(() => {
        return this.state().offers?.filter(offer => offer.type === OfferType.Additional) || [];
    });

    findOfferByCustomizationAndChoice(customizationCode: string, choiceCode: string): AdditionalOfferData | undefined {
        return this.offers().find(offer => offer.customizationCode === customizationCode && offer.choiceCode === choiceCode);
    }

    findOffersByCustomization(customizationCode: string): AdditionalOfferData[] {
        return this.offers().filter(offer => offer.customizationCode === customizationCode);
    }

    findDropdownSelectedOffer(customizationCode: string): AdditionalOfferData | undefined {
        return this.offers().find(offer => offer.customizationCode === customizationCode);
    }

    addOffer(offer: AdditionalOfferData): void {
        const currentState = this.state();
        const existingOfferIndex = currentState?.offers?.findIndex(o => o.choiceCode === offer.choiceCode);
        if (existingOfferIndex > -1) {
            currentState.offers[existingOfferIndex] = offer;
        } else {
            currentState.offers.push(offer);
        }
        this.updateData(currentState);
    }

    removeOffer(choiceCode: string): void {
        const currentState = this.state();
        currentState.offers = currentState.offers?.filter(o => o.choiceCode !== choiceCode);
        this.updateData(currentState);
    }

    addABPoffer(abpEnabled: boolean): void {
        const abpOffer = this.wirelessPdResponseService.getABPAdditionalOfferData(abpEnabled);
        this.addOffer(abpOffer);
        this.removeOffer(abpEnabled ? this.PAPERLESS_BILLING_CHOICE_CODE : this.ABP_CHOICE_CODE);
    }

    hasABPOffer(): boolean {
        const abpOffer = this.wirelessPdResponseService.getABPAdditionalOfferData(true);
        return this.offers().some(offer => offer.choiceCode === abpOffer.choiceCode);
    }

    getABPOffer(): AdditionalOfferData | undefined {
        const abpOffer = this.wirelessPdResponseService.getABPAdditionalOfferData(true);
        return this.findOfferByCustomizationAndChoice(abpOffer.customizationCode, abpOffer.choiceCode);
    }

    protected override saveLocalDraft(): void {
        this.flowStateService.updateData({ [this.stateKey]: this.state() });
    }

    resetData() {
        this.state.set(null);
        this.saveLocalDraft();
    }
}
